import * as React from "react";
import { Modal, Tabs } from "antd";
import { v4 as uuidv4 } from "uuid";
import { PlusOutlined } from "@ant-design/icons";
import { HetznerNoDedicatedServerNodePool } from "./noDedicServNodePool";
import { HetznerDedicatedServerNodePool } from "./dedicServNodePool";
import { getDefaultUnmanagedNodePoolInput } from "../../../draft/utils/utils";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { cloneDeep } from "lodash";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { TabLabel } from "../../../../workload/draft/tabLabel";
import { Mk8sCluster } from "../../../../../schema/types/mk8s/entity/mk8s";

type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

interface Props {
  mk8s?: Mk8sCluster;
}
export const HetznerDedicatedServerNodePools: React.FC<Props> = ({ mk8s }) => {
  const { id } = useCpFormContext();

  const dedicatedServerNodePools = useCpFormValue("hetzner.dedicatedServerNodePools");
  const activeKey =
    useCpFormValue(`meta.hetzner.activeKeyDedicatedServerNodePool`) || dedicatedServerNodePools[0]?.id || "";

  function onEdit(targetKey: React.MouseEvent | React.KeyboardEvent | string, action: "add" | "remove") {
    if (action === "add") {
      add();
    } else {
      Modal.confirm({
        title: "Remove Dedicated Server Node Pool",
        content: `Configuration that was made on this node pool will be lost`,
        onOk() {
          remove(targetKey);
        },
      });
    }
  }

  function add() {
    const newActiveKey = uuidv4();

    const _dedicatedServerNodePools = cloneDeep(dedicatedServerNodePools);
    _dedicatedServerNodePools.push({
      ...getDefaultUnmanagedNodePoolInput(),
      id: newActiveKey,
    });
    formSetValue(id, `hetzner.dedicatedServerNodePools`, _dedicatedServerNodePools);

    formSetValue(id, `meta.hetzner.activeKeyDedicatedServerNodePool`, newActiveKey);
  }

  function remove(targetKey: TargetKey) {
    let newActiveKey = activeKey;
    let lastIndex = -1;
    let removeIndex = -1;

    dedicatedServerNodePools.forEach((item, i) => {
      if (item.id === targetKey) {
        lastIndex = i - 1;
        removeIndex = i;
      }
    });

    if (removeIndex >= 0) {
      const _nodePools = cloneDeep(dedicatedServerNodePools);
      _nodePools.splice(removeIndex, 1);
      formSetValue(id, `hetzner.dedicatedServerNodePools`, _nodePools);
      formTriggerValidation(id);

      if (dedicatedServerNodePools.length > 1 && newActiveKey === targetKey) {
        if (lastIndex >= 0) {
          newActiveKey = dedicatedServerNodePools[lastIndex].id!;
        } else {
          newActiveKey = dedicatedServerNodePools[removeIndex + 1]?.id || dedicatedServerNodePools[0]?.id || "";
        }
        formSetValue(id, `meta.hetzner.activeKeyDedicatedServerNodePool`, newActiveKey);
      }
    }
  }

  function onChange(newActiveKey: string) {
    formSetValue(id, `meta.hetzner.activeKeyDedicatedServerNodePool`, newActiveKey);
  }

  return (
    <div className="cpln-form-section cpln-ant-card-container cpln-ant-tabs-container">
      <Tabs
        type={"editable-card"}
        items={dedicatedServerNodePools.map((np, i) => {
          const label = np.name || "Dedicated Server Node Pool";

          return {
            key: np.id!,
            label: <TabLabel label={label} names={[`hetzner.dedicatedServerNodePools.${i}`]} showDirty />,
            closable: true,
            children: <HetznerDedicatedServerNodePool index={i} />,
          };
        })}
        onChange={onChange}
        activeKey={activeKey}
        onEdit={onEdit}
        addIcon={
          <div className="flex items-center gap-2 px-4">
            <div>Add</div>
            <PlusOutlined />
          </div>
        }
      />
      {dedicatedServerNodePools.length < 1 ? <HetznerNoDedicatedServerNodePool /> : null}
    </div>
  );
};
